import type { ReactNode } from "react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import cx from "ui/cx";
import useAppContext from "ui/hooks/useAppContext";
import { PopoverContainerContext } from "ui/hooks/usePopoverContainer";
import IconClose from "ui/icons/IconClose";
import styles from "./Modal.module.scss";

export interface ModalProps {
  children?: ReactNode;
  open: boolean;
  onClose?: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  title?: ReactNode;
  titleActions?: ReactNode;
  noDefaultStyle?: boolean;
  noCloseOnOutsideClick?: boolean;
  scrollModal?: boolean;
  fullHeight?: boolean;
  submitting?: boolean;
  size?: "small" | "medium" | "large" | "full";
  className?: string;
  header?: ReactNode;
  headerClassName?: string;
  footer?: ReactNode;
  footerClassName?: string;
  variants?: ModalVariant | ModalVariant[];
}

// various styles that can be applied in combo
type ModalVariant = "footerButtons" | "bodyPadding";

const applyVariant = (variant: ModalVariant, variants?: ModalVariant | ModalVariant[]) =>
  variant === variants || (Array.isArray(variants) && variants.includes(variant)) ? styles[variant] : null;

export default function Modal({
  title,
  titleActions,
  children,
  open,
  onClose,
  noDefaultStyle,
  noCloseOnOutsideClick,
  scrollModal = false,
  fullHeight = false,
  submitting = false,
  size = "medium",
  className,
  header,
  headerClassName,
  footer,
  footerClassName,
  variants,
}: ModalProps) {
  // keep local open state so onClose() executes after modal close
  const [localOpen, setLocalOpen] = useState(open);
  const { modalContainerRef } = useAppContext();
  const modalContent = useRef<HTMLDivElement>(null);
  const [popoverContainer, setPopoverContainer] = useState<HTMLDivElement | null>(null);

  /**
   * Callback ref captures the modal DOM element for setPopoverContainer
   * This lets tooltips and other popovers inherit the modal's stacking context
   * so they appear above the modal and clicks on them don't close the modal.
   * Also lets popovers inherit any cascading styling and CSS variables
   */
  // TODO: extend this to other portalled components like popovers, select, etc.
  const setModalRef = useCallback((el: HTMLDivElement | null) => {
    modalContent.current = el;
    setPopoverContainer(el);
  }, []);

  useEffect(() => {
    setLocalOpen(open);
  }, [open]);

  useEffect(() => {
    if (!localOpen && onClose) {
      onClose();
    }
  }, [localOpen]);

  if (!modalContainerRef?.current) {
    return null;
  }

  // avoids creating a portal for every instance (slow)
  if (!open) {
    return null;
  }

  const classNames = cx(styles.modal, styles[size], localOpen && styles.showModal, className);

  return ReactDOM.createPortal(
    <div
      className={cx(
        styles.modalScreen,
        {
          styles,
          showModal: localOpen,
          fullHeight,
        },
        scrollModal && styles.scrollable,
      )}
      onMouseDown={(e) => {
        if (!noCloseOnOutsideClick && !submitting) {
          const target = e.target;
          if (!(target instanceof Element)) {
            return;
          }
          const clickedInsideModal = modalContent.current?.contains(target) ?? false;
          // Handles clicking in select listbox that has been portaled outside of the modal
          // TODO: refactor how modals containing portals (tooltip, select, popover, etc) are handled
          const clickedInListbox = target.closest('[role="listbox"]') !== null;

          if (!clickedInsideModal && !clickedInListbox) {
            setLocalOpen(false);
          }
        }
      }}
    >
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <PopoverContainerContext.Provider value={popoverContainer}>
          {noDefaultStyle === true ? (
            <div style={{ padding: 0 }} className={classNames} ref={setModalRef} id="modal">
              {header && <div className={cx(styles.internalHeader, headerClassName)}>{header}</div>}
              {children}
              {footer && (
                <div className={cx(styles.footer, applyVariant("footerButtons", variants), footerClassName)}>
                  {footer}
                </div>
              )}
            </div>
          ) : (
            <div className={classNames} ref={setModalRef} id="modal">
              <div className={styles.header}>
                <div className={styles.title}>{title}</div>
                {titleActions && <>{titleActions}</>}
                <IconClose
                  size={18}
                  className={cx(styles.closeIcon, submitting && styles.disabled)}
                  onClick={() => {
                    if (!submitting) {
                      setLocalOpen(false);
                    }
                  }}
                />
              </div>
              {header && <div className={cx(styles.internalHeader, headerClassName)}>{header}</div>}
              <div className={cx(styles.bodyWrapper, applyVariant("bodyPadding", variants))}>{children}</div>
              {footer && (
                <div className={cx(styles.footer, applyVariant("footerButtons", variants), footerClassName)}>
                  {footer}
                </div>
              )}
            </div>
          )}
        </PopoverContainerContext.Provider>
      </div>
    </div>,
    modalContainerRef.current,
  );
}
